import { HeadingTypeEnum } from 'common/enums/HeadingTypeEnum'
import { ContentTableInterface } from 'common/types/entities/ContentTableInterface'
import { HeadlineInterface } from 'common/types/entities/HeadlineInterface'
import { OldTextEntityInterface } from 'common/types/entities/OldEntityInterface'
import {
  getHeadingTypeFromHtml,
  getTextFromHtml,
} from 'common/utils/textEditorUtils'
import { convertFromRaw } from 'draft-js'

interface GetProcessedHeadlinesProps {
  headings: HeadlineInterface[] | OldTextEntityInterface[]
  entity: ContentTableInterface
  isClient?: boolean
}

export const getClientRawContentState = (
  heading: HeadlineInterface | OldTextEntityInterface,
) => {
  const contentRawState = convertFromRaw(
    JSON.parse(
      'rawContentState' in heading
        ? heading.rawContentState
        : heading.options.rawContentState,
    ),
  )

  return {
    type: contentRawState.getFirstBlock().getType() as HeadingTypeEnum,
    text: contentRawState.getPlainText(),
  }
}

export const getPublisherRawContentState = (
  heading: HeadlineInterface | OldTextEntityInterface,
) => {
  const contentRawState = JSON.parse(
    'rawContentState' in heading
      ? heading.rawContentState
      : heading.options.rawContentState,
  )

  // each line break produces a new block of text
  const text = contentRawState.blocks.reduce(
    (result: string, { text }: { text: string }): string => result.concat(text),
    '',
  )

  const type =
    Object.values(HeadingTypeEnum).find(
      headingType => contentRawState.blocks[0].type === headingType,
    ) || HeadingTypeEnum.HeaderFive

  return {
    type,
    text,
  }
}

export const getProcessedHeadlines = ({
  headings,
  entity,
  isClient = true,
}: GetProcessedHeadlinesProps) =>
  headings
    .map((heading: HeadlineInterface | OldTextEntityInterface) => {
      let headlineType: HeadingTypeEnum
      let content: string

      if ('content' in heading && heading.content) {
        headlineType = getHeadingTypeFromHtml(
          heading.content,
        ) as HeadingTypeEnum
        content = getTextFromHtml(heading.content ?? '')
      } else {
        if (isClient) {
          const { type, text } = getClientRawContentState(heading)
          headlineType = type
          content = text
        } else {
          const { type, text } = getPublisherRawContentState(heading)
          headlineType = type
          content = text
        }
      }

      return {
        headline: heading,
        headlineType,
        content,
      }
    })
    .filter(processedHeadline =>
      entity.headersTypes.includes(processedHeadline.headlineType),
    )
